import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DndContext, DragEndEvent, DragMoveEvent, DragStartEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';

import * as api from '@/features/wiki/api';
import { WikiPage, WikiRevision, WikiSpace } from '@/features/wiki/types';
import { FlatPage, buildFlat, childCount, getProjection, positionForDrop, subtreeIds } from '@/features/wiki/tree';
import { applyLoadedDoc, useWikiDoc, useWikiTree } from '@/features/wiki/realtime';
import { diffContent } from '@/features/wiki/diff';
import { wikiPath, wikiSpacePath, wikiPagePath } from '@/constants/routes';
import WikiSpaceSettings from '@/components/wiki-space-settings';
import { SearchField } from '@/components/ui/SearchField';
import { useIdentity } from '@/features/identity/identity';
import { getActorId } from '@/utils/http';
import { isDesktopApp } from '@/features/identity/desktop-updates';
import AreaNav from '@/components/area-nav';
import IdentityMenu from '@/features/identity/identity-menu';
import NotificationBell from '@/components/notification-bell';
import WikiEditor, { SaveState, WikiReadOnly, WikiCommentCmd } from '@/components/wiki-editor';
import WikiCommentsRail from '@/components/wiki-comments-rail';
import WikiRelatedItems from '@/components/wiki-related-items';
import { useWikiComments } from '@/features/wiki/comments-realtime';
import { Member } from '@/features/backlog/types';
import MemberAvatar from '@/components/member-avatar';
import { Popover } from '@/components/ui/Popover';
import { useAppData } from '@/app-data';
import { Button } from '@/components/ui/Button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { Spinner } from '@/components/ui/Spinner';
import { Chevron, Plus } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

const INDENT = 16;

const HistoryIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M3 12a9 9 0 1 0 2.6-6.4L3 8M3 4v4h4M12 7.5V12l3 2" />
  </svg>
);
const CopyLinkIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
/** |<- collapse the pages rail. */
const CollapseRailIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 4v16M15 9l-3 3 3 3" />
  </svg>
);
/** A drawer/sidebar reveal button (shown when the pages rail is collapsed). */
const ExpandRailIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 4v16M13 9l3 3-3 3" />
  </svg>
);
const CommentIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);
const MoreIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
  </svg>
);
const CheckIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M5 12l5 5L20 7" />
  </svg>
);

/** A crisp document glyph for the page tree (emoji render inconsistently across OS/browsers). */
const DocIcon = ({ className }: { className?: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </svg>
);

/**
 * The Wiki area (Confluence-like) at /wiki and /wiki/:pageId — a two-pane layout: a reorderable page /
 * sub-page tree rail + a live collaborative editor for the open page. The tree stays live for everyone
 * via the "wiki" realtime room; each open page is a separate collaborative Yjs room.
 */
const CogIcon = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

/** Top-level wiki area: routes between the spaces index, a space's page tree, and a legacy-id redirect. */
export default function WikiArea() {
  const { spaceSlug, pageSlug, pageId } = useParams();
  if (pageId) return <LegacyPageRedirect pageId={pageId} />;
  if (!spaceSlug) return <WikiSpacesIndex />;
  return <WikiSpaceView spaceSlug={spaceSlug} pageSlug={pageSlug ?? null} />;
}

/** Resolves a legacy /wiki/p/:pageId link to the friendly /wiki/<space>/<page> URL. */
function LegacyPageRedirect({ pageId }: { pageId: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await api.getWikiPage(pageId);
        const space = await api.getWikiSpace(page.space_id);
        // Preserve the query (e.g. ?thread=<id> from a comment-mention email deep-link).
        if (!cancelled) navigate(wikiPagePath(space.slug, page.slug) + window.location.search, { replace: true });
      } catch {
        if (!cancelled) navigate(wikiPath(), { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageId, navigate]);
  return (
    <div className="flex h-screen items-center justify-center bg-ink text-fg">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

/** The spaces index — a card grid of wiki spaces (modeled on the whiteboards index). */
function WikiSpacesIndex() {
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState<WikiSpace[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const busyRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        setSpaces(await api.listWikiSpaces());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useWikiTree('', {
    onUpsert: () => undefined,
    onDelete: () => undefined,
    onSpaceUpsert: (s) =>
      setSpaces((prev) => {
        const i = prev.findIndex((x) => x.id === s.id);
        if (i === -1) return [...prev, s];
        const n = prev.slice();
        n[i] = { ...n[i], ...s };
        return n;
      }),
    onSpaceDelete: (ids) => setSpaces((prev) => prev.filter((s) => !ids.includes(s.id))),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = [...spaces].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    return needle ? sorted.filter((s) => s.name.toLowerCase().includes(needle)) : sorted;
  }, [spaces, q]);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || busyRef.current) return;
    busyRef.current = true;
    try {
      const s = await api.createWikiSpace({ name: trimmed });
      navigate(wikiSpacePath(s.slug));
    } finally {
      busyRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-ink text-fg">
      <header className="sticky top-0 z-30 border-b border-line bg-ink/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between gap-4 px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/15 text-brand">
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M2 3.2C2 2.54 2.54 2 3.2 2h9.6c.66 0 1.2.54 1.2 1.2v6.4c0 .66-.54 1.2-1.2 1.2H9l-2.4 2.6V10.8H3.2c-.66 0-1.2-.54-1.2-1.2V3.2Z" fill="currentColor" />
              </svg>
            </span>
            <span className="text-[14px] font-semibold text-fg-strong">spark-board</span>
          </div>
          <AreaNav />
          <div className="flex items-center gap-1"><NotificationBell />{isDesktopApp() ? null : <IdentityMenu />}</div>
        </div>
      </header>
      <main className="mx-auto max-w-[1100px] px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[22px] font-bold text-fg-strong">Wiki spaces</h1>
            <p className="mt-1 text-[13px] text-fg-muted">Open a space, or create a new one.</p>
          </div>
          <div className="flex items-center gap-2">
            <SearchField value={q} onChange={setQ} placeholder="Search spaces…" />
            {!creating ? (
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                <Plus /> New space
              </Button>
            ) : null}
          </div>
        </div>
        {creating ? (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-line bg-surface-inset p-2">
            <input
              autoFocus
              className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-[14px] text-fg outline-none focus:border-line-strong"
              placeholder="Space name (e.g. Engineering)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') create();
                if (e.key === 'Escape') {
                  setCreating(false);
                  setName('');
                }
              }}
            />
            <Button variant="primary" size="sm" disabled={!name.trim()} onClick={create}>
              Create
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setCreating(false); setName(''); }}>
              Cancel
            </Button>
          </div>
        ) : null}
        {loading ? (
          <div className="flex justify-center py-24">
            <Spinner className="h-6 w-6" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface-raised px-6 py-16 text-center text-[13px] text-fg-faint">
            {q.trim() ? `No spaces match “${q.trim()}”.` : 'No spaces yet — create your first one above.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s) => (
              <Link key={s.id} to={wikiSpacePath(s.slug)} className="group flex flex-col rounded-2xl border border-line bg-surface-raised p-5 transition-colors hover:border-line-strong hover:bg-layer">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand/15 text-[18px] text-brand">
                  {s.icon || <DocIcon />}
                </div>
                <div className="text-[15px] font-semibold text-fg-strong group-hover:text-fg">{s.name}</div>
                {s.description ? <div className="mt-1 line-clamp-2 text-[12px] text-fg-muted">{s.description}</div> : null}
                <div className="mt-3 text-[11px] text-fg-faint">{s.page_count} page{s.page_count === 1 ? '' : 's'}</div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/** A single space's two-pane view: its reorderable page tree + the open page editor. */
function WikiSpaceView({ spaceSlug, pageSlug }: { spaceSlug: string; pageSlug: string | null }) {
  const navigate = useNavigate();
  const { me } = useIdentity();
  const selfId = me?.id ?? getActorId() ?? '';

  const [space, setSpace] = useState<WikiSpace | null>(null);
  const [spaceError, setSpaceError] = useState(false);
  const [pages, setPages] = useState<WikiPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [treeCollapsed, setTreeCollapsed] = useState(false);

  // Resolve the space from its slug.
  useEffect(() => {
    let cancelled = false;
    setSpaceError(false);
    (async () => {
      try {
        const s = await api.getWikiSpaceBySlug(spaceSlug);
        if (!cancelled) setSpace(s);
      } catch {
        if (!cancelled) setSpaceError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spaceSlug]);

  // Keep the URL in sync with the space's slug — a rename re-derives it (local save or a realtime upsert
  // from another client). Preserves the open page. The old slug stops resolving on the server.
  useEffect(() => {
    if (space && space.slug !== spaceSlug) {
      navigate(pageSlug ? wikiPagePath(space.slug, pageSlug) : wikiSpacePath(space.slug), { replace: true });
    }
  }, [space, spaceSlug, pageSlug, navigate]);

  // The rail auto-collapses once a page finishes loading (content focus), and re-opens at the space root.
  const collapseAfterLoad = useCallback(() => { window.setTimeout(() => setTreeCollapsed(true), 160); }, []);
  useEffect(() => { if (!pageSlug) setTreeCollapsed(false); }, [pageSlug]);

  const upsertPage = useCallback(
    (p: WikiPage) => {
      // ignore events for OTHER spaces (the tree is scoped to this one)
      if (space && p.space_id && p.space_id !== space.id) return;
      setPages((prev) => {
        const i = prev.findIndex((x) => x.id === p.id);
        if (i === -1) return [...prev, p];
        const next = prev.slice();
        next[i] = { ...next[i], ...p };
        return next;
      });
    },
    [space],
  );
  const removePages = useCallback(
    (ids: string[]) => {
      const set = new Set(ids);
      setPages((prev) => prev.filter((p) => !set.has(p.id)));
      const active = pages.find((p) => p.slug === pageSlug);
      if (active && set.has(active.id) && space) navigate(wikiSpacePath(space.slug), { replace: true });
    },
    [pages, pageSlug, space, navigate],
  );

  useEffect(() => {
    if (!space) return;
    let cancelled = false;
    (async () => {
      try {
        const ps = await api.listWikiSpacePages(space.id);
        if (!cancelled) setPages(ps);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
  }, [space]);

  useWikiTree(selfId, {
    onUpsert: upsertPage,
    onDelete: removePages,
    onSpaceUpsert: (s) => {
      if (space && s.id === space.id) setSpace((prev) => (prev ? { ...prev, ...s } : s));
    },
    onSpaceDelete: (ids) => {
      if (space && ids.includes(space.id)) navigate(wikiPath(), { replace: true });
    },
  });

  const createPage = useCallback(
    async (parentId: string | null) => {
      if (!space) return;
      const siblings = pages.filter((p) => (p.parent_id ?? null) === parentId);
      const position = siblings.length ? Math.max(...siblings.map((p) => p.position)) + 1 : Date.now();
      const page = await api.createWikiPage({ title: '', parent_id: parentId, position, space_id: space.id });
      upsertPage(page);
      navigate(wikiPagePath(space.slug, page.slug));
    },
    [pages, space, upsertPage, navigate],
  );

  if (spaceError) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-ink text-fg">
        <div className="text-[15px] font-semibold text-fg-soft">Space not found</div>
        <Button variant="primary" size="sm" onClick={() => navigate(wikiPath())}>Back to spaces</Button>
      </div>
    );
  }

  const active = pageSlug && space ? pages.find((p) => p.slug === pageSlug) ?? null : null;

  // Breadcrumb trail from the space root down through the open page's ancestors (the current page's own
  // title is the big heading, so it's not repeated here). Shown above the presence avatars.
  const crumbs: { label: string; to: string }[] = space
    ? (() => {
        const byId = new Map(pages.map((p) => [p.id, p]));
        const anc: WikiPage[] = [];
        let cur = active?.parent_id ? byId.get(active.parent_id) : null;
        while (cur) { anc.unshift(cur); cur = cur.parent_id ? byId.get(cur.parent_id) : null; }
        return [{ label: space.name, to: wikiSpacePath(space.slug) }, ...anc.map((p) => ({ label: p.title || 'Untitled', to: wikiPagePath(space.slug, p.slug) }))];
      })()
    : [];

  return (
    <div className="flex h-screen flex-col bg-ink text-fg">
      <header className="relative z-40 flex h-14 shrink-0 items-center justify-between gap-4 border-b border-line bg-ink/80 px-6 backdrop-blur">
        <div className="flex min-w-0 items-center gap-2">
          <Link to={wikiPath()} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand" title="All spaces">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M2 3.2C2 2.54 2.54 2 3.2 2h9.6c.66 0 1.2.54 1.2 1.2v6.4c0 .66-.54 1.2-1.2 1.2H9l-2.4 2.6V10.8H3.2c-.66 0-1.2-.54-1.2-1.2V3.2Z" fill="currentColor" />
            </svg>
          </Link>
          <Link to={wikiPath()} className="shrink-0 text-[13px] text-fg-faint hover:text-fg-muted">Spaces</Link>
          <span className="shrink-0 text-fg-ghost">/</span>
          <span className="truncate text-[14px] font-semibold text-fg-strong">{space?.name ?? '…'}</span>
          {space ? (
            <button type="button" onClick={() => setShowSettings(true)} className="ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-fg-faint hover:bg-layer hover:text-fg" title="Space settings">
              <CogIcon />
            </button>
          ) : null}
        </div>
        <AreaNav />
        <div className="flex items-center gap-1"><NotificationBell />{isDesktopApp() ? null : <IdentityMenu />}</div>
      </header>

      <div className="flex min-h-0 flex-1">
        <div className={cn('shrink-0 overflow-hidden border-r border-line bg-surface transition-[width] duration-300 ease-in-out', treeCollapsed ? 'w-12' : 'w-[268px]')}>
          <PageTree pages={pages} loading={loading} activeSlug={pageSlug} spaceSlug={spaceSlug} onCreate={createPage} onLocalUpsert={upsertPage} onLocalRemove={removePages} railCollapsed={treeCollapsed} onToggleRail={() => setTreeCollapsed((v) => !v)} />
        </div>
        <main className="min-w-0 flex-1 overflow-hidden">
          {active && space ? (
            <WikiPageView key={active.id} page={active} spaceSlug={space.slug} selfId={selfId} self={{ id: selfId, name: me?.name ?? 'You', color: me?.color ?? '#ff791b' }} onRenamed={upsertPage} onDeleted={removePages} crumbs={crumbs} onLoaded={collapseAfterLoad} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <div className="text-[15px] font-semibold text-fg-soft">{loading ? 'Loading…' : pages.length ? 'Select a page' : 'This space is empty'}</div>
              <p className="max-w-sm text-[13px] text-fg-muted">Create pages and sub-pages, edit together in real time, and organise them in the sidebar.</p>
              <Button variant="primary" size="sm" onClick={() => createPage(null)}>
                <Plus /> New page
              </Button>
            </div>
          )}
        </main>
      </div>

      {showSettings && space ? (
        <WikiSpaceSettings space={space} onClose={() => setShowSettings(false)} onSaved={(s) => setSpace(s)} onDeleted={() => { setShowSettings(false); navigate(wikiPath()); }} />
      ) : null}
    </div>
  );
}

// ---- the reorderable page tree rail ----
function PageTree({
  pages,
  loading,
  activeSlug,
  spaceSlug,
  onCreate,
  onLocalUpsert,
  onLocalRemove,
  railCollapsed,
  onToggleRail,
}: {
  pages: WikiPage[];
  loading: boolean;
  activeSlug: string | null;
  spaceSlug: string;
  onCreate: (parentId: string | null) => void;
  onLocalUpsert: (p: WikiPage) => void;
  onLocalRemove: (ids: string[]) => void;
  railCollapsed: boolean;
  onToggleRail: () => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dragId, setDragId] = useState<string | null>(null);
  const [projParent, setProjParent] = useState<string | null>(null);
  const [offsetX, setOffsetX] = useState(0);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const flat = useMemo(() => buildFlat(pages, collapsed), [pages, collapsed]);
  // While dragging, exclude the active page's subtree from the projection reference (it moves with it).
  const flatForProj = useMemo(() => {
    if (!dragId) return flat;
    const sub = subtreeIds(pages, dragId);
    return flat.filter((f) => !sub.has(f.page.id) || f.page.id === dragId);
  }, [flat, dragId, pages]);

  const toggle = (id: string) => setCollapsed((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const onDragStart = (e: DragStartEvent) => setDragId(String(e.active.id));
  const onDragMove = (e: DragMoveEvent) => {
    setOffsetX(e.delta.x);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || !dragId) return;
    const proj = getProjection(flatForProj, dragId, overId, e.delta.x, INDENT);
    setProjParent(proj.parentId);
  };
  const onDragEnd = async (e: DragEndEvent) => {
    const activeIdStr = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    setDragId(null);
    setProjParent(null);
    if (!overId) return;
    // Reject dropping a page onto itself or one of its own descendants (a cycle / self-promote).
    if (subtreeIds(pages, activeIdStr).has(overId)) return;
    const proj = getProjection(flatForProj, activeIdStr, overId, offsetX, INDENT);
    setOffsetX(0);
    const overIndex = flatForProj.findIndex((f) => f.page.id === overId);
    const position = positionForDrop(flatForProj, overIndex, proj.parentId, activeIdStr);
    const page = pages.find((p) => p.id === activeIdStr);
    if (!page) return;
    if ((page.parent_id ?? null) === proj.parentId && page.position === position) return;
    // optimistic
    onLocalUpsert({ ...page, parent_id: proj.parentId, position });
    try {
      const fresh = await api.moveWikiPage(activeIdStr, proj.parentId, position);
      onLocalUpsert(fresh);
    } catch {
      onLocalUpsert(page); // revert
    }
  };

  return (
    <aside className="flex h-full w-[268px] shrink-0 flex-col">
      {/* Toggle sits IN FRONT of "Pages" and stays put — at the collapsed width (w-12) only it shows. */}
      <div className="flex items-center gap-1 px-2.5 py-2.5">
        <button type="button" onClick={onToggleRail} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-layer hover:text-fg" title={railCollapsed ? 'Show pages' : 'Hide pages'}>
          {railCollapsed ? <ExpandRailIcon /> : <CollapseRailIcon />}
        </button>
        <span className={cn('flex-1 truncate text-[12px] font-semibold uppercase tracking-wide text-fg-faint transition-opacity duration-200', railCollapsed ? 'opacity-0' : 'opacity-100')}>Pages</span>
        <button type="button" onClick={() => onCreate(null)} className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-layer hover:text-fg', railCollapsed && 'pointer-events-none opacity-0')} title="New page">
          <Plus />
        </button>
      </div>
      <div className={cn('min-h-0 flex-1 overflow-y-auto px-1.5 pb-3 transition-opacity duration-200', railCollapsed ? 'pointer-events-none opacity-0' : 'opacity-100')}>
        {loading ? (
          <div className="flex justify-center py-8"><Spinner className="h-4 w-4 text-fg-faint" /></div>
        ) : flat.length === 0 ? (
          <button type="button" onClick={() => onCreate(null)} className="mx-1.5 mt-1 flex w-[calc(100%-12px)] items-center gap-2 rounded-lg border border-dashed border-line px-2.5 py-2 text-[12px] text-fg-muted transition-colors hover:border-line-strong hover:text-fg">
            <Plus /> New page
          </button>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={() => { setDragId(null); setProjParent(null); }}>
            <SortableContext items={flat.map((f) => f.page.id)} strategy={verticalListSortingStrategy}>
              {flat.map((f) => (
                <PageRow
                  key={f.page.id}
                  flat={f}
                  pages={pages}
                  activeSlug={activeSlug}
                  spaceSlug={spaceSlug}
                  collapsed={collapsed.has(f.page.id)}
                  hasKids={childCount(pages, f.page.id) > 0}
                  isDragging={dragId === f.page.id}
                  onToggle={() => toggle(f.page.id)}
                  onCreateChild={() => onCreate(f.page.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </aside>
  );
}

function PageRow({
  flat,
  pages,
  activeSlug,
  spaceSlug,
  collapsed,
  hasKids,
  isDragging,
  onToggle,
  onCreateChild,
}: {
  flat: FlatPage;
  pages: WikiPage[];
  activeSlug: string | null;
  spaceSlug: string;
  collapsed: boolean;
  hasKids: boolean;
  isDragging: boolean;
  onToggle: () => void;
  onCreateChild: () => void;
}) {
  const navigate = useNavigate();
  const { page, depth } = flat;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: page.id });
  const isActive = activeSlug === page.slug;
  const style = { transform: CSS.Transform.toString(transform), transition, paddingLeft: 6 + depth * INDENT };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('group/row relative flex items-center gap-1 rounded-lg pr-1', isActive ? 'bg-layer-strong' : 'hover:bg-layer', isDragging && 'opacity-40')}
      {...attributes}
      {...listeners}
    >
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-faint transition-transform', !hasKids && 'invisible', !collapsed && 'rotate-90')}
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        <Chevron />
      </button>
      <button type="button" onClick={() => navigate(wikiPagePath(spaceSlug, page.slug))} className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left">
        <DocIcon className="shrink-0 text-fg-faint" />
        <span className={cn('min-w-0 flex-1 truncate text-[13px]', isActive ? 'font-medium text-fg-strong' : 'text-fg-soft')}>{page.title || 'Untitled'}</span>
      </button>
      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100">
        <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onCreateChild(); }} className="flex h-5 w-5 items-center justify-center rounded text-fg-muted hover:bg-layer-strong hover:text-fg" title="Add sub-page">
          <Plus />
        </button>
      </div>
    </div>
  );
}

// ---- the open page: title + presence + collaborative editor + history ----
function WikiPageView({ page, spaceSlug, selfId, self, onRenamed, onDeleted, crumbs, onLoaded }: { page: WikiPage; spaceSlug: string; selfId: string; self: { id: string; name: string; color: string }; onRenamed: (p: WikiPage) => void; onDeleted: (ids: string[]) => void; crumbs: { label: string; to: string }[]; onLoaded: () => void }) {
  const { members } = useAppData();
  const navigate = useNavigate();
  const ydoc = useMemo(() => new Y.Doc(), [page.id]);
  const awareness = useMemo(() => new Awareness(ydoc), [ydoc]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [seedContent, setSeedContent] = useState<unknown>(null);
  const [title, setTitle] = useState(page.title);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [showHistory, setShowHistory] = useState(false);
  const restoreRef = useRef<((content: unknown) => void) | null>(null);
  const titleFocused = useRef(false);
  const [copied, setCopied] = useState(false);

  // ---- anchored comments ----
  const { threads, loaded: commentsLoaded, upsert, remove } = useWikiComments(page.id, selfId);
  const [railOpen, setRailOpen] = useState(false);
  const [activeThread, setActiveThread] = useState<string | null>(null);
  const [pending, setPending] = useState<{ id: string; quote: string } | null>(null);
  const commentCmdRef = useRef<WikiCommentCmd | null>(null);
  const resolvedIds = useMemo(() => new Set(threads.filter((t) => t.resolved).map((t) => t.id)), [threads]);
  const openCount = threads.filter((t) => !t.resolved).length;
  const [searchParams] = useSearchParams();

  // ?thread=<id> deep-link: open the rail, select + scroll to the thread once loaded.
  const deepThread = searchParams.get('thread');
  useEffect(() => {
    if (!deepThread || !commentsLoaded) return;
    setRailOpen(true);
    setActiveThread(deepThread);
    const t = window.setTimeout(() => commentCmdRef.current?.scrollTo(deepThread), 300);
    return () => window.clearTimeout(t);
  }, [deepThread, commentsLoaded]);

  const createThread = useCallback(
    async (threadId: string, quote: string, body: string) => {
      try {
        const t = await api.createWikiThread(page.id, { id: threadId, quote, body });
        upsert(t);
        commentCmdRef.current?.commitPending(t.id); // promote the pending highlight to a real mark
        setActiveThread(t.id);
        setPending(null);
      } catch {
        commentCmdRef.current?.clearPending(); // creation failed — drop the pending highlight
        setPending(null);
      }
    },
    [page.id, upsert],
  );
  const reply = useCallback(async (threadId: string, body: string) => { const t = await api.addWikiComment(threadId, body); upsert(t); }, [upsert]);
  const resolve = useCallback((threadId: string, resolved: boolean) => { api.resolveWikiThread(threadId, resolved).then(upsert).catch(() => undefined); }, [upsert]);
  const del = useCallback((threadId: string) => { commentCmdRef.current?.removeMark(threadId); api.deleteWikiThread(threadId).then((r) => remove(r.ids)).catch(() => undefined); }, [remove]);
  const cancelPending = useCallback(() => { commentCmdRef.current?.clearPending(); setPending(null); }, []);

  // ---- page "…" menu (delete) ----
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  // In-app confirm modal (NOT window.confirm — that no-ops in the Tauri desktop app).
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deletePage = useCallback(() => {
    setPageMenuOpen(false);
    setConfirmDelete(true);
  }, []);
  const doDeletePage = useCallback(async () => {
    setDeleting(true);
    try {
      const res = await api.deleteWikiPage(page.id);
      onDeleted(res.ids); // navigates away (component unmounts) — no state reset needed on success
    } catch {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [page.id, onDeleted]);
  useEffect(() => {
    if (!pageMenuOpen) return undefined;
    const close = () => setPageMenuOpen(false);
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [pageMenuOpen]);
  const copyLink = useCallback(() => {
    const url = window.location.origin + wikiPagePath(spaceSlug, page.slug);
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }).catch(() => undefined);
  }, [spaceSlug, page.slug]);

  // Re-sync the title input to a REMOTE rename (page.title changed via the live tree) — but only while
  // the input isn't focused, so it never yanks text out from under the person actively renaming.
  useEffect(() => {
    if (!titleFocused.current) setTitle(page.title);
  }, [page.title]);

  const loadOnce = useCallback(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadError(false);
    api
      .getWikiPage(page.id)
      .then((full) => {
        if (cancelled) return;
        applyLoadedDoc(ydoc, full.doc);
        // An imported page arrives with body `content` but no Yjs `doc` — hand it to the editor to
        // materialize once (a normal page always has `doc`, so this stays null and nothing is seeded).
        const docEmpty = !full.doc || (typeof full.doc === 'string' && full.doc.length === 0);
        const c = full.content as { content?: unknown[] } | null | undefined;
        setSeedContent(docEmpty && c && Array.isArray(c.content) && c.content.length > 0 ? full.content : null);
        setLoaded(true);
      })
      // A failed load must NOT mount an empty editable doc — the first keystroke would autosave an
      // empty state over the real (unloaded) content. Show a retry instead.
      .catch(() => !cancelled && setLoadError(true));
    return () => {
      cancelled = true;
    };
  }, [page.id, ydoc]);

  useEffect(loadOnce, [loadOnce]);

  // Tell the space view the page finished loading (once), so it can auto-collapse the rail.
  const firedLoad = useRef(false);
  useEffect(() => { if (loaded && !firedLoad.current) { firedLoad.current = true; onLoaded(); } }, [loaded, onLoaded]);

  // Reflect the page title in the browser tab (live as you rename); restore on leave.
  useEffect(() => {
    const prev = document.title;
    document.title = `${(title || 'Untitled').trim()} · Wiki · Spark Board`;
    return () => { document.title = prev; };
  }, [title]);

  // Destroy the doc/awareness when leaving the page (frees the CRDT + presence state).
  useEffect(() => () => { awareness.destroy(); ydoc.destroy(); }, [ydoc, awareness]);

  const rt = useWikiDoc({ pageId: page.id, ydoc, awareness, selfId, enabled: loaded });

  const commitTitle = useCallback(() => {
    const t = title.trim();
    if (t === page.title) return;
    api
      .updateWikiPage(page.id, { title: t })
      .then((p) => {
        onRenamed(p);
        // A title change may re-derive the slug (e.g. the first real title on a blank page) — follow the
        // URL to the fresh slug so a reload / copy-link uses it and the open page doesn't drop out.
        if (p.slug && p.slug !== page.slug) navigate(wikiPagePath(spaceSlug, p.slug) + window.location.search, { replace: true });
      })
      .catch(() => setTitle(page.title));
  }, [title, page.id, page.title, page.slug, spaceSlug, onRenamed, navigate]);

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 overflow-y-auto">
        {/* Title + presence share the toolbar's surface, with a hairline separating them from it. */}
        <div className="border-b border-line bg-surface pb-4 pt-6">
        {crumbs.length ? (
          <nav className="wiki-col mb-1.5 flex items-center gap-1 overflow-hidden text-[11.5px] text-fg-faint">
            {crumbs.map((c, i) => (
              <Fragment key={c.to}>
                {i > 0 ? <span className="shrink-0 text-fg-ghost">/</span> : null}
                <Link to={c.to} className="max-w-[220px] shrink-0 truncate rounded px-0.5 transition-colors hover:text-fg-muted">{c.label}</Link>
              </Fragment>
            ))}
          </nav>
        ) : null}
        <div className="wiki-col mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <PresenceRow memberIds={rt.presence.length ? rt.presence : [selfId]} />
          </div>
          <div className="flex items-center gap-1.5 text-[12px]">
            <SaveBadge state={saveState} connected={rt.connected} />
            <button type="button" onClick={() => setRailOpen((v) => { if (v) setActiveThread(null); return !v; })} className={cn('flex h-7 items-center gap-1 rounded-md px-2 transition-colors hover:bg-layer', railOpen ? 'text-accent' : 'text-fg-muted hover:text-fg')} title="Comments">
              <CommentIcon />
              {openCount > 0 ? <span className="tnum text-[11px] font-semibold">{openCount}</span> : null}
            </button>
            <button type="button" onClick={() => setShowHistory(true)} className="flex h-7 w-7 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-layer hover:text-fg" title="Page history">
              <HistoryIcon />
            </button>
            <button type="button" onClick={copyLink} className={cn('flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-layer', copied ? 'text-ok' : 'text-fg-muted hover:text-fg')} title={copied ? 'Link copied' : 'Copy link to page'}>
              {copied ? <CheckIcon /> : <CopyLinkIcon />}
            </button>
            <div className="relative">
              <button type="button" onClick={(e) => { e.stopPropagation(); setPageMenuOpen((v) => !v); }} className="flex h-7 w-7 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-layer hover:text-fg" title="More">
                <MoreIcon />
              </button>
              {pageMenuOpen ? (
                <div className="absolute right-0 top-8 z-50 w-40 overflow-hidden rounded-lg border border-line bg-surface-raised py-1 shadow-xl" onPointerDown={(e) => e.stopPropagation()}>
                  <button type="button" onClick={deletePage} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-danger hover:bg-danger/10">Delete page</button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="wiki-col">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onFocus={() => { titleFocused.current = true; }}
            onBlur={() => { titleFocused.current = false; commitTitle(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            placeholder="Untitled"
            className="w-full bg-transparent text-[30px] font-bold text-fg-strong outline-none placeholder:text-fg-ghost"
          />
        </div>
        </div>

        {loadError ? (
          <div className="wiki-col">
            <div className="flex flex-col items-center gap-3 rounded-xl border border-line bg-surface-inset py-12 text-center">
              <span className="text-[13px] text-fg-muted">Couldn’t load this page.</span>
              <Button variant="outline" size="sm" onClick={loadOnce}>Retry</Button>
            </div>
          </div>
        ) : loaded ? (
          <WikiEditor
            pageId={page.id}
            ydoc={ydoc}
            awareness={awareness}
            user={self}
            members={members}
            onSaveState={setSaveState}
            restoreRef={restoreRef}
            seedContent={seedContent}
            activeThreadId={activeThread}
            resolvedThreadIds={resolvedIds}
            onActivateThread={(id) => { cancelPending(); setRailOpen(true); setActiveThread(id); commentCmdRef.current?.scrollTo(id); }}
            onRequestNewThread={(id, quote) => { setRailOpen(true); setPending({ id, quote }); }}
            commentCmdRef={commentCmdRef}
          />
        ) : (
          <div className="flex justify-center py-16"><Spinner className="h-5 w-5 text-fg-faint" /></div>
        )}

        {loaded ? <WikiRelatedItems pageId={page.id} refreshKey={saveState === 'saved'} /> : null}

        {showHistory ? (
          <HistoryPanel
            pageId={page.id}
            members={members}
            onRestore={(content) => {
              restoreRef.current?.(content);
              setShowHistory(false);
            }}
            onClose={() => setShowHistory(false)}
          />
        ) : null}
      </div>

      {/* Animated width so the comments rail slides in/out smoothly (kept mounted; clipped when closed).
          `flex` makes the inner aside a flex child so it stretches to the full height. */}
      <div className={cn('flex h-full shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out', railOpen ? 'w-[320px]' : 'w-0')} aria-hidden={!railOpen}>
        <WikiCommentsRail
          threads={threads}
          members={members}
          meId={selfId}
          activeThreadId={activeThread}
          pending={pending}
          onActivate={(id) => { cancelPending(); setActiveThread(id); if (id) commentCmdRef.current?.scrollTo(id); }}
          onReply={reply}
          onResolve={resolve}
          onDelete={del}
          onSubmitPending={(body) => { if (pending) createThread(pending.id, pending.quote, body); }}
          onCancelPending={cancelPending}
          onClose={() => { cancelPending(); setRailOpen(false); setActiveThread(null); }}
        />
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => { if (!deleting) setConfirmDelete(false); }}
        onConfirm={doDeletePage}
        title="Delete page"
        message={<>Delete <b className="text-fg-strong">{page.title || 'this page'}</b> and any sub-pages? This can’t be undone.</>}
        confirmLabel="Delete page"
        busy={deleting}
      />
    </div>
  );
}

function SaveBadge({ state, connected }: { state: SaveState; connected: boolean }) {
  const label = state === 'saving' ? 'Saving…' : state === 'error' ? 'Save failed' : state === 'saved' ? 'Saved' : connected ? 'Live' : 'Offline';
  const color = state === 'error' ? 'text-danger' : state === 'saving' ? 'text-fg-muted' : connected ? 'text-ok' : 'text-fg-faint';
  return (
    <span className={cn('flex items-center gap-1.5 font-medium', color)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', state === 'error' ? 'bg-danger' : connected ? 'bg-ok' : 'bg-fg-faint')} />
      {label}
    </span>
  );
}

function PresenceRow({ memberIds }: { memberIds: string[] }) {
  const { members } = useAppData();
  const { me } = useIdentity();
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const uniqueIds = useMemo(() => Array.from(new Set(memberIds)), [memberIds]);
  const roster = uniqueIds.map((id) => byId.get(id)).filter((m): m is Member => !!m);
  const meMember = me ? roster.find((m) => m.id === me.id) : undefined;
  const others = roster.filter((m) => m.id !== me?.id);
  const total = roster.length;
  const shown = uniqueIds.slice(0, 6);

  return (
    <Popover
      align="left"
      panelClassName="min-w-[240px]"
      trigger={({ toggle, open }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={`${total} ${total === 1 ? 'person' : 'people'} here — show everyone`}
          className={cn('flex items-center gap-2.5 rounded-full p-0.5 pr-1 transition-colors hover:bg-layer', open && 'bg-layer')}
        >
          {/* The overlap (-space-x) stays scoped to the avatars so it doesn't pull the "N here" label. */}
          <div className="flex items-center -space-x-1.5">
            {shown.map((id) => {
              const m = byId.get(id);
              return m ? <MemberAvatar key={id} member={m} size={24} className="ring-2 ring-surface" /> : null;
            })}
          </div>
          {total > 1 ? <span className="text-[12px] text-fg-faint">{total} here</span> : null}
        </button>
      )}
    >
      {() => (
        <div className="min-w-[220px]">
          <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">{total} online</div>
          <div className="mb-1 h-px bg-line" />
          <ul>
            {meMember ? <PresenceRosterRow member={meMember} you /> : null}
            {others.map((m) => (
              <PresenceRosterRow key={m.id} member={m} />
            ))}
          </ul>
        </div>
      )}
    </Popover>
  );
}

function PresenceRosterRow({ member, you = false }: { member: Member; you?: boolean }) {
  return (
    <li className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
      <MemberAvatar member={member} size={28} />
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-fg-soft">
        {member.name}
        {you ? <span className="ml-1.5 text-[11px] font-normal text-fg-faint">You</span> : null}
      </span>
      <span className="h-2 w-2 shrink-0 rounded-full bg-ok" title="Online" aria-label="Online" />
    </li>
  );
}

// ---- history / revisions panel (right-side sheet) ----
function HistoryPanel({ pageId, members, onRestore, onClose }: { pageId: string; members: Member[]; onRestore: (content: unknown) => void; onClose: () => void }) {
  const [revs, setRevs] = useState<WikiRevision[] | null>(null);
  const [selected, setSelected] = useState<WikiRevision | null>(null);
  const [prevContent, setPrevContent] = useState<unknown>(null);

  useEffect(() => {
    api.listWikiRevisions(pageId).then(setRevs).catch(() => setRevs([]));
  }, [pageId]);

  // Open a revision AND fetch the one immediately before it, so the rich render can highlight what THIS
  // version changed (green added / red removed) inline.
  const openRev = (rev: WikiRevision) => {
    setPrevContent(null);
    api.getWikiRevision(rev.id).then(setSelected).catch(() => undefined);
    const list = revs ?? [];
    const older = list[list.findIndex((r) => r.id === rev.id) + 1]; // list is newest-first → next is older
    if (older) api.getWikiRevision(older.id).then((p) => setPrevContent(p.content ?? null)).catch(() => setPrevContent(null));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-[640px] flex-col border-l border-line bg-surface" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-[14px] font-semibold text-fg-strong">Page history</span>
          <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-[12px] text-fg-muted hover:bg-layer hover:text-fg">Close</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {revs === null ? (
            <div className="flex justify-center py-10"><Spinner className="h-5 w-5 text-fg-faint" /></div>
          ) : revs.length === 0 ? (
            <div className="px-4 py-10 text-center text-[13px] text-fg-faint">No versions captured yet — they’re saved automatically as the page is edited.</div>
          ) : !selected ? (
            <ul className="p-2">
              {revs.map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => openRev(r)} className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-layer">
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium text-fg-soft">{r.author_name || 'Someone'}</span>
                      <span className="block text-[11px] text-fg-faint">{new Date(r.created_at).toLocaleString()}</span>
                    </span>
                    <span className="shrink-0 text-[11px] text-fg-faint">{(r.title || 'Untitled').slice(0, 24)}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4">
              <button type="button" onClick={() => setSelected(null)} className="mb-3 text-[12px] text-fg-muted hover:text-fg">← All versions</button>
              <div className="mb-1 text-[24px] font-bold text-fg-strong">{selected.title || 'Untitled'}</div>
              <div className="mb-3 text-[11px] text-fg-faint">{selected.author_name || 'Someone'} · {new Date(selected.created_at).toLocaleString()}</div>
              {/* Rich render with the same node views as the live page (images, tables, checklists,
                  embeds, mentions) — with the change vs. the previous version highlighted INLINE
                  (green added / red removed), not as a separate raw diff. */}
              <div className="wiki-history-render rounded-lg border border-line bg-surface-raised px-4 py-3">
                <WikiReadOnly content={diffContent(prevContent, selected.content)} members={members} />
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="primary" size="sm" onClick={() => onRestore(selected.content)}>
                  Restore this version
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** A minimal line-level diff (old → new) for the version view: removed lines red, added lines green. */
function DiffView({ oldText, newText }: { oldText: string; newText: string }) {
  const rows = useMemo(() => lineDiff(oldText, newText), [oldText, newText]);
  return (
    <div className="max-h-[50vh] overflow-auto rounded-lg border border-line bg-surface-inset p-3 font-mono text-[11.5px] leading-relaxed">
      {rows.length === 0 ? (
        <div className="text-fg-faint">No text content.</div>
      ) : (
        rows.map((r, i) => (
          <div key={i} className={cn('whitespace-pre-wrap', r.kind === 'add' && 'bg-ok/10 text-ok', r.kind === 'del' && 'bg-danger/10 text-danger line-through', r.kind === 'same' && 'text-fg-muted')}>
            <span className="mr-2 select-none text-fg-ghost">{r.kind === 'add' ? '+' : r.kind === 'del' ? '−' : ' '}</span>
            {r.text || ' '}
          </div>
        ))
      )}
    </div>
  );
}

/** LCS line diff → ordered rows tagged same/add/del (this version = old, current = new). */
function lineDiff(oldText: string, newText: string): { kind: 'same' | 'add' | 'del'; text: string }[] {
  const a = oldText.split('\n');
  const b = newText.split('\n');
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: { kind: 'same' | 'add' | 'del'; text: string }[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ kind: 'same', text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ kind: 'del', text: a[i] }); i++; }
    else { out.push({ kind: 'add', text: b[j] }); j++; }
  }
  while (i < n) { out.push({ kind: 'del', text: a[i] }); i++; }
  while (j < m) { out.push({ kind: 'add', text: b[j] }); j++; }
  return out;
}
